/**
 * Onboarding step definitions per site type and progress management.
 *
 * eCom: 8 steps, Booking: 7 steps, Landing: 6 steps.
 * Steps are adaptive — only relevant steps shown per site type.
 */

export type SiteType = 'ecommerce' | 'booking' | 'landing';

export interface OnboardingStepDef {
  readonly id: string;
  readonly label: string;       // Hebrew display label
  readonly required: boolean;
}

export interface OnboardingStepState {
  readonly id: string;
  readonly completed: boolean;
  readonly skipped: boolean;
  readonly completedAt: string | null;
}

export interface OnboardingProgress {
  readonly tenantId: string;
  readonly siteType: SiteType;
  readonly steps: readonly OnboardingStepState[];
  readonly currentStep: string;
  readonly greetingText: string | null;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly lastActivityAt: string;
  readonly alertSentAt: string | null;
}

/** Step definitions per site type — ordered. */
export const ONBOARDING_STEPS: Record<SiteType, readonly OnboardingStepDef[]> = {
  ecommerce: [
    { id: 'welcome', label: '\u05D1\u05E8\u05D5\u05DA \u05D4\u05D1\u05D0', required: true },
    { id: 'business_info', label: '\u05E4\u05E8\u05D8\u05D9 \u05E2\u05E1\u05E7', required: true },
    { id: 'logo_upload', label: '\u05DC\u05D5\u05D2\u05D5', required: false },
    { id: 'payment_setup', label: '\u05D0\u05DE\u05E6\u05E2\u05D9 \u05EA\u05E9\u05DC\u05D5\u05DD', required: true },
    { id: 'shipping_config', label: '\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05DE\u05E9\u05DC\u05D5\u05D7', required: true },
    { id: 'first_product', label: '\u05DE\u05D5\u05E6\u05E8 \u05E8\u05D0\u05E9\u05D5\u05DF', required: true },
    { id: 'whatsapp_connect', label: '\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4', required: false },
    { id: 'review_publish', label: '\u05E4\u05E8\u05E1\u05D5\u05DD', required: true },
  ],
  booking: [
    { id: 'welcome', label: '\u05D1\u05E8\u05D5\u05DA \u05D4\u05D1\u05D0', required: true },
    { id: 'business_info', label: '\u05E4\u05E8\u05D8\u05D9 \u05E2\u05E1\u05E7', required: true },
    { id: 'logo_upload', label: '\u05DC\u05D5\u05D2\u05D5', required: false },
    { id: 'services_setup', label: '\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD', required: true },
    { id: 'work_hours', label: '\u05E9\u05E2\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA', required: true },
    { id: 'whatsapp_connect', label: '\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4', required: false },
    { id: 'review_publish', label: '\u05E4\u05E8\u05E1\u05D5\u05DD', required: true },
  ],
  landing: [
    { id: 'welcome', label: '\u05D1\u05E8\u05D5\u05DA \u05D4\u05D1\u05D0', required: true },
    { id: 'business_info', label: '\u05E4\u05E8\u05D8\u05D9 \u05E2\u05E1\u05E7', required: true },
    { id: 'logo_upload', label: '\u05DC\u05D5\u05D2\u05D5', required: false },
    { id: 'contact_form', label: '\u05D8\u05D5\u05E4\u05E1 \u05DC\u05D9\u05D3\u05D9\u05DD', required: true },
    { id: 'whatsapp_connect', label: '\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4', required: false },
    { id: 'review_publish', label: '\u05E4\u05E8\u05E1\u05D5\u05DD', required: true },
  ],
};

function getStepDefs(siteType: SiteType): readonly OnboardingStepDef[] {
  return ONBOARDING_STEPS[siteType];
}

function buildGreeting(businessName: string, stepCount: number): string {
  return `\u05E9\u05DC\u05D5\u05DD ${businessName}! \u05D4\u05D2\u05E2\u05EA \u05DC\u05DE\u05E7\u05D5\u05DD \u05D4\u05E0\u05DB\u05D5\u05DF. \u05D1\u05D5\u05D0\u05D5 \u05E0\u05DB\u05D9\u05DF \u05D0\u05EA \u05D4\u05D0\u05EA\u05E8 \u05E9\u05DC\u05DA \u05D1-${stepCount} \u05E6\u05E2\u05D3\u05D9\u05DD \u05E4\u05E9\u05D5\u05D8\u05D9\u05DD.`;
}

function buildInitialSteps(
  defs: readonly OnboardingStepDef[],
): readonly OnboardingStepState[] {
  return defs.map((def) => ({
    id: def.id,
    completed: false,
    skipped: false,
    completedAt: null,
  }));
}

export function createInitialProgress(
  tenantId: string,
  siteType: SiteType,
  businessName: string,
): OnboardingProgress {
  const defs = getStepDefs(siteType);
  const now = new Date().toISOString();
  return {
    tenantId,
    siteType,
    steps: buildInitialSteps(defs),
    currentStep: 'welcome',
    greetingText: buildGreeting(businessName, defs.length),
    startedAt: now,
    completedAt: null,
    lastActivityAt: now,
    alertSentAt: null,
  };
}

function findNextIncompleteRequired(
  steps: readonly OnboardingStepState[],
  defs: readonly OnboardingStepDef[],
  afterIndex: number,
): string | null {
  for (let i = afterIndex + 1; i < steps.length; i++) {
    const step = steps[i];
    const def = defs[i];
    if (step !== undefined && def !== undefined && def.required && !step.completed && !step.skipped) {
      return step.id;
    }
  }
  // Wrap around — check from beginning
  for (let i = 0; i <= afterIndex; i++) {
    const step = steps[i];
    const def = defs[i];
    if (step !== undefined && def !== undefined && def.required && !step.completed && !step.skipped) {
      return step.id;
    }
  }
  return null;
}

function findNextIncomplete(
  steps: readonly OnboardingStepState[],
  afterIndex: number,
): string | null {
  for (let i = afterIndex + 1; i < steps.length; i++) {
    const step = steps[i];
    if (step !== undefined && !step.completed && !step.skipped) {
      return step.id;
    }
  }
  return null;
}

export function completeStep(
  progress: OnboardingProgress,
  stepId: string,
): OnboardingProgress {
  const defs = getStepDefs(progress.siteType);
  const now = new Date().toISOString();
  const stepIndex = progress.steps.findIndex((s) => s.id === stepId);
  if (stepIndex === -1) {
    throw new Error(`Unknown step: ${stepId}`);
  }

  const newSteps = progress.steps.map((s) =>
    s.id === stepId
      ? { ...s, completed: true, completedAt: now }
      : s,
  );

  // Find next step: prefer next incomplete required, fallback to any incomplete
  const nextRequired = findNextIncompleteRequired(newSteps, defs, stepIndex);
  const nextAny = findNextIncomplete(newSteps, stepIndex);
  const nextStep = nextRequired ?? nextAny ?? stepId;

  const allRequiredDone = defs.every((def, i) => {
    if (!def.required) return true;
    const step = newSteps[i];
    return step !== undefined && step.completed;
  });

  return {
    ...progress,
    steps: newSteps,
    currentStep: nextStep,
    lastActivityAt: now,
    completedAt: allRequiredDone ? now : null,
  };
}

export function skipStep(
  progress: OnboardingProgress,
  stepId: string,
): OnboardingProgress {
  const defs = getStepDefs(progress.siteType);
  const stepIndex = progress.steps.findIndex((s) => s.id === stepId);
  if (stepIndex === -1) {
    throw new Error(`Unknown step: ${stepId}`);
  }

  const def = defs[stepIndex];
  if (def !== undefined && def.required) {
    throw new Error(`Cannot skip required step: ${stepId}`);
  }

  const now = new Date().toISOString();
  const newSteps = progress.steps.map((s) =>
    s.id === stepId
      ? { ...s, skipped: true }
      : s,
  );

  const nextRequired = findNextIncompleteRequired(newSteps, defs, stepIndex);
  const nextAny = findNextIncomplete(newSteps, stepIndex);
  const nextStep = nextRequired ?? nextAny ?? stepId;

  return {
    ...progress,
    steps: newSteps,
    currentStep: nextStep,
    lastActivityAt: now,
  };
}

export function isOnboardingComplete(progress: OnboardingProgress): boolean {
  return progress.completedAt !== null;
}

export function getCompletionPercentage(progress: OnboardingProgress): number {
  const total = progress.steps.length;
  if (total === 0) return 100;
  const done = progress.steps.filter((s) => s.completed || s.skipped).length;
  return Math.round((done / total) * 100);
}

export function getStepDefsForType(siteType: SiteType): readonly OnboardingStepDef[] {
  return getStepDefs(siteType);
}
